import {useCallback} from "react";

import {
    Box,
    Divider,
    LinearProgress,
    Sheet,
    Typography,
} from "@mui/joy";

import FolderOpenIcon from "@mui/icons-material/FolderOpen";

import useLogFileStore from "../../stores/logFileStore";
import {handleErrorWithNotification} from "../../stores/notificationStore";
import useUiStore from "../../stores/uiStore";
import useViewStore from "../../stores/viewStore";
import {FILE_TYPE_NAME} from "../../typings/file";
import {UI_ELEMENT} from "../../typings/states";
import {CURSOR_CODE} from "../../typings/worker";
import {openFile} from "../../utils/file";
import {isDisabled} from "../../utils/states";
import ExportLogsButton from "./ExportLogsButton";
import MenuBarIconButton from "./MenuBarIconButton";
import NavigationBar from "./NavigationBar";
import TimestampQueryContainer from "./TimestampQueryContainer";

import "./index.css";


/**
 * Renders a menu bar which displays file information and provides navigation and settings buttons.
 *
 * @return
 */
const MenuBar = () => {
    const fileTypeInfo = useLogFileStore((state) => state.fileTypeInfo);
    const fileName = useLogFileStore((state) => state.fileName);
    const uiState = useUiStore((state) => state.uiState);
    const isTimestampQueryVisible = null !== fileTypeInfo &&
        FILE_TYPE_NAME.PLAIN_TEXT !== fileTypeInfo.name;

    const handleOpenFile = useCallback(() => {
        openFile((file) => {
            (async () => {
                const {loadFile} = useLogFileStore.getState();
                await loadFile(file);
                const {filterLogs} = useViewStore.getState();
                filterLogs();
                const {loadPageByCursor} = useViewStore.getState();
                await loadPageByCursor({code: CURSOR_CODE.LAST_EVENT, args: null});
            })().catch(handleErrorWithNotification);
        });
    }, []);

    return (
        <>
            <Sheet className={"menu-bar"}>
                <div className={"menu-bar-logo-container"}>
                    <img
                        alt={"yscope-small-logo"}
                        src={"./favicon.svg"}
                        width={20}/>
                </div>

                <Divider orientation={"vertical"}/>
                <MenuBarIconButton
                    disabled={isDisabled(uiState, UI_ELEMENT.OPEN_FILE_BUTTON)}
                    tooltipPlacement={"bottom-start"}
                    tooltipTitle={"Open file"}
                    onClick={handleOpenFile}
                >
                    <FolderOpenIcon className={"menu-bar-open-file-icon"}/>
                </MenuBarIconButton>
                <Divider orientation={"vertical"}/>

                <Box
                    className={"menu-bar-filename-container"}
                    title={fileName}
                >
                    <Typography
                        className={"menu-bar-filename-left-split"}
                        level={"body-md"}
                    >
                        {fileName.slice(0, fileName.length / 2)}
                    </Typography>
                    <Typography
                        className={"menu-bar-filename-right-split"}
                        level={"body-md"}
                    >
                        {fileName.slice(fileName.length / 2)}
                    </Typography>
                </Box>

                {isTimestampQueryVisible && <TimestampQueryContainer/>}

                <NavigationBar/>
                <Divider orientation={"vertical"}/>

                <ExportLogsButton/>
            </Sheet>
            {(false === isDisabled(uiState, UI_ELEMENT.PROGRESS_BAR)) &&
                <LinearProgress
                    className={"menu-bar-loading-progress"}
                    size={"sm"}
                    thickness={2}/>}
        </>
    );
};

export default MenuBar;
